import { useMemo, useState } from 'react';

import { Dayjs } from 'dayjs';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

import { Flexbox } from 'src/components/Alignments';
import { dailyStatsByUserState, dailyStatsState } from 'src/states/dailyStats';
import { projectTotalStats } from 'src/states/stats';
import { Modality } from 'src/types/api/data/image';
import { DailyStatsProps } from 'src/types/client/stats';
import { isValidDate, oneMonthAgo, today } from 'src/utils/date';

import StatsDailyByUsers from './StatsDailyByUsers';
import StatsDailyProgress from './StatsDailyProgress';
import TotalProgressPie from './TotalProgressPie';
import UserProgressBarChart from './UserProgressBarChart';

const PageContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionCard = styled(Paper)`
  border-radius: 14px;
  padding: 20px;
  background-color: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled(Typography)`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #0a0a0a;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TwoColumnGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const StyledTable = styled(Table)`
  & .MuiTableCell-head {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #6a7282;
    background-color: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
  }

  & .MuiTableCell-body {
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    color: #0a0a0a;
    border-bottom: 1px solid #f3f4f6;
  }
`;

const StatusChip = styled(Box)<{ status: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props: { status: string }) => {
    if (props.status === 'In Activity') return '#e0f2fe';
    if (props.status === 'Not Started') return '#f3f4f6';
    return '#dcfce7';
  }};
  color: ${(props: { status: string }) => {
    if (props.status === 'In Activity') return '#0369a1';
    if (props.status === 'Not Started') return '#6b7280';
    return '#15803d';
  }};
`;

const SummaryGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SummaryCard = styled(Box)`
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const SummaryLabel = styled(Typography)`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: #6a7282;
  margin-bottom: 4px;
`;

const SummaryValue = styled(Typography)`
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #0a0a0a;
`;

interface StatsTabPageProps {
  projectId: string;
  modality: Modality;
}

const DEFAULT_END_DATE = today;
const DEFAULT_START_DATE = oneMonthAgo;

export default function StatsTabPage({
  projectId,
  modality,
}: StatsTabPageProps): JSX.Element {
  const [startDate] = useState<Dayjs | null>(DEFAULT_START_DATE);
  const [endDate] = useState<Dayjs | null>(DEFAULT_END_DATE);
  const totalStats = useRecoilValue(projectTotalStats);

  const params = useMemo<DailyStatsProps>(
    () => ({
      projectId,
      modality,
      type: 'total',
      dateFrom:
        startDate && isValidDate(startDate) ? startDate.toISOString() : '',
      dateTo: endDate && isValidDate(endDate) ? endDate.toISOString() : '',
    }),
    [projectId, modality, startDate, endDate]
  );

  const dailyStats = useRecoilValueLoadable(dailyStatsState(params));
  const dailyStatsByUser = useRecoilValueLoadable(
    dailyStatsByUserState(params)
  );

  // Calculate user statistics
  const userStats = useMemo(() => {
    if (dailyStatsByUser.state !== 'hasValue') return [];

    return dailyStatsByUser.contents.map(user => {
      const totalJobs = user.data.reduce((sum, day) => sum + day.y, 0);
      const completed = totalJobs; // Assuming all counted jobs are completed
      const remaining = 0;
      const progress = totalJobs > 0 ? 100 : 0;
      const status =
        progress === 100
          ? 'Completed'
          : progress > 0
          ? 'In Activity'
          : 'Not Started';

      return {
        username: user.id,
        totalJobs,
        completed,
        remaining,
        progress,
        status,
      };
    });
  }, [dailyStatsByUser]);

  // Calculate summary statistics
  const summary = useMemo(() => {
    const totalUsers = userStats.length;
    const totalJobs = userStats.reduce((sum, user) => sum + user.totalJobs, 0);
    const totalCompleted = userStats.reduce(
      (sum, user) => sum + user.completed,
      0
    );
    const avgProgress =
      totalUsers > 0
        ? (
            userStats.reduce((sum, user) => sum + user.progress, 0) / totalUsers
          ).toFixed(1)
        : '0.0';

    return { totalUsers, totalJobs, totalCompleted, avgProgress };
  }, [userStats]);

  if (totalStats.total === 0) {
    return (
      <PageContainer>
        <Flexbox $justify="center" $align="center" style={{ height: 400 }}>
          <Typography variant="h5">
            There is no stats available for the project
          </Typography>
        </Flexbox>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Overall Progress and Overview by User */}
      <TwoColumnGrid>
        {/* Overall Progress */}
        <SectionCard>
          <SectionTitle>Overall Progress</SectionTitle>
          <Box style={{ height: 350 }}>
            <TotalProgressPie data={totalStats.stats} />
          </Box>
        </SectionCard>

        {/* Overview of annotation progress by user */}
        <SectionCard>
          <SectionTitle>Overview of annotation progress by user</SectionTitle>
          <Box style={{ height: 200, marginBottom: 16 }}>
            <UserProgressBarChart data={userStats} />
          </Box>

          <TableContainer>
            <StyledTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Total Jobs</TableCell>
                  <TableCell align="right">Completed</TableCell>
                  <TableCell align="right">Remaining</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {userStats.map(user => (
                  <TableRow key={user.username}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell align="right">{user.totalJobs}</TableCell>
                    <TableCell align="right">{user.completed}</TableCell>
                    <TableCell align="right">{user.remaining}</TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinearProgress
                          variant="determinate"
                          value={user.progress}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption">
                          {user.progress}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={user.status}>
                        {user.status}
                      </StatusChip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>

          <SummaryGrid>
            <SummaryCard>
              <SummaryLabel>Total Users</SummaryLabel>
              <SummaryValue>{summary.totalUsers}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <SummaryLabel>Total Jobs</SummaryLabel>
              <SummaryValue>{summary.totalJobs}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <SummaryLabel>Completed</SummaryLabel>
              <SummaryValue>{summary.totalCompleted}</SummaryValue>
            </SummaryCard>
            <SummaryCard>
              <SummaryLabel>Average Progress</SummaryLabel>
              <SummaryValue>{summary.avgProgress}%</SummaryValue>
            </SummaryCard>
          </SummaryGrid>
        </SectionCard>
      </TwoColumnGrid>

      {/* Daily Annotation Progress by User */}
      <SectionCard>
        <SectionTitle>Daily Annotation Progress by User</SectionTitle>
        <Box style={{ height: 400, marginBottom: 16 }}>
          <StatsDailyByUsers
            data={
              dailyStatsByUser.state === 'hasValue'
                ? dailyStatsByUser.contents
                : []
            }
          />
        </Box>

        <TableContainer>
          <StyledTable size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                {dailyStatsByUser.state === 'hasValue' &&
                  dailyStatsByUser.contents.map(user => (
                    <TableCell key={user.id} align="right">
                      {user.id}
                    </TableCell>
                  ))}
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dailyStats.state === 'hasValue' &&
                dailyStats.contents.map((dayStat, idx) => (
                  <TableRow key={dayStat.date}>
                    <TableCell>{dayStat.date}</TableCell>
                    {dailyStatsByUser.state === 'hasValue' &&
                      dailyStatsByUser.contents.map(user => (
                        <TableCell key={user.id} align="right">
                          {user.data[idx]?.y || 0}
                        </TableCell>
                      ))}
                    <TableCell align="right">{dayStat.count}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </SectionCard>

      {/* Daily Annotation Progress */}
      <SectionCard>
        <SectionTitle>Daily Annotation Progress</SectionTitle>
        <Box style={{ height: 400, marginBottom: 16 }}>
          <StatsDailyProgress
            data={dailyStats.state === 'hasValue' ? dailyStats.contents : []}
          />
        </Box>

        <TableContainer>
          <StyledTable size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Daily Annotations</TableCell>
                <TableCell align="right">Cumulative Total</TableCell>
                <TableCell>Progress Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dailyStats.state === 'hasValue' &&
                dailyStats.contents.map((dayStat, idx) => {
                  const cumulative = dailyStats.contents
                    .slice(0, idx + 1)
                    .reduce((sum, d) => sum + d.count, 0);
                  return (
                    <TableRow key={dayStat.date}>
                      <TableCell>{dayStat.date}</TableCell>
                      <TableCell align="right">{dayStat.count}</TableCell>
                      <TableCell align="right">{cumulative}</TableCell>
                      <TableCell>
                        <StatusChip status="In Activity">
                          In Activity
                        </StatusChip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </SectionCard>
    </PageContainer>
  );
}
